import { DestroyRef, Directive, ElementRef, HostListener, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup } from '@angular/forms';
import { merge } from 'rxjs';
import { ShDatepickerElement } from '../types/sh-datepicker-element.type';
import { formatShDatepickerValue } from '../utils/sh-datepicker.utils';

@Directive({
    selector: 'sh-datepicker[formGroup][formControlNameStart][formControlNameEnd]'
})
export class ShDatepickerRangeFormBridgeDirective implements OnInit {
    public readonly formGroup = input.required<FormGroup>();
    public readonly formControlNameStart = input.required<string>();
    public readonly formControlNameEnd = input.required<string>();

    private readonly elementRef = inject<ElementRef<ShDatepickerElement>>(ElementRef);
    private readonly destroyRef = inject(DestroyRef);

    public ngOnInit(): void {
        queueMicrotask(() => this.syncElementValueFromForm());

        const startControl = this.getControl(this.formControlNameStart());
        const endControl = this.getControl(this.formControlNameEnd());
        merge(startControl.valueChanges, endControl.valueChanges)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.syncElementValueFromForm());
    }

    @HostListener('value-changed')
    protected handleRangeChanged(): void {
        const [startDate = null, endDate = null] = this.elementRef.nativeElement.standardValue ?? [];
        const startControl = this.getControl(this.formControlNameStart());
        const endControl = this.getControl(this.formControlNameEnd());

        if (
            !startDate &&
            !endDate &&
            !startControl.value &&
            !endControl.value &&
            startControl.pristine &&
            endControl.pristine
        ) {
            return;
        }

        startControl.setValue(startDate);
        endControl.setValue(endDate);
        startControl.markAsTouched();
        endControl.markAsTouched();
        this.formGroup().updateValueAndValidity();
    }

    private getControl(controlName: string): AbstractControl {
        return this.formGroup().get(controlName) as AbstractControl;
    }

    private syncElementValueFromForm(): void {
        const startDate = this.getControl(this.formControlNameStart()).value as Date | null;
        const endDate = this.getControl(this.formControlNameEnd()).value as Date | null;

        this.elementRef.nativeElement.value =
            startDate && endDate
                ? `${formatShDatepickerValue(startDate, this.elementRef.nativeElement)} – ${formatShDatepickerValue(endDate, this.elementRef.nativeElement)}`
                : '';
    }
}
